import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@sunfish/lib/utils';

const radioCardVariants = cva(
	'flex items-start gap-3 p-3 rounded-md border border-transparent cursor-pointer transition-colors',
	{
		variants: {
			variant: {
				default: 'hover:bg-muted/50 data-[state=checked]:border-primary data-[state=checked]:bg-primary/5',
				success:
					'hover:bg-muted/50 data-[state=checked]:border-green-500 data-[state=checked]:bg-green-50 dark:data-[state=checked]:bg-green-950 dark:data-[state=checked]:border-green-600',
				destructive:
					'hover:bg-muted/50 data-[state=checked]:border-red-500 data-[state=checked]:bg-red-50 dark:data-[state=checked]:bg-red-950 dark:data-[state=checked]:border-red-600'
			}
		},
		defaultVariants: {
			variant: 'default'
		}
	}
);

const radioIndicatorVariants = cva(
	'aspect-square h-4 w-4 rounded-full border shadow-sm flex items-center justify-center mt-0.5 shrink-0 transition-colors',
	{
		variants: {
			variant: {
				default: 'border-primary',
				success:
					'border-muted-foreground group-data-[state=checked]:border-green-500 dark:group-data-[state=checked]:border-green-400',
				destructive:
					'border-muted-foreground group-data-[state=checked]:border-red-500 dark:group-data-[state=checked]:border-red-400'
			}
		},
		defaultVariants: {
			variant: 'default'
		}
	}
);

const radioDotVariants = cva('h-2 w-2 rounded-full', {
	variants: {
		variant: {
			default: 'bg-primary',
			success: 'bg-green-500 dark:bg-green-400',
			destructive: 'bg-red-500 dark:bg-red-400'
		}
	},
	defaultVariants: {
		variant: 'default'
	}
});

type RadioCardProps = React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> &
	VariantProps<typeof radioCardVariants> & {
		title: string;
		description?: string;
		children?: React.ReactNode;
	};

const RadioCard = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Item>, RadioCardProps>(
	({ className, variant, title, description, children, ...props }, ref) => {
		return (
			<RadioGroupPrimitive.Item ref={ref} className={cn('group', radioCardVariants({ variant }), className)} {...props}>
				<div className={cn(radioIndicatorVariants({ variant }))}>
					<RadioGroupPrimitive.Indicator className={cn(radioDotVariants({ variant }))} />
				</div>
				<div className="flex-1 min-w-0">
					<div className="font-medium text-sm">{title}</div>
					{description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
					{children}
				</div>
			</RadioGroupPrimitive.Item>
		);
	}
);
RadioCard.displayName = 'RadioCard';

export { RadioCard, radioCardVariants };
